import { useState, useCallback, useMemo } from "react";
import { useSession } from "@/hooks/use-session";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { differenceInDays } from "date-fns";
import { SubscriptionStatus } from "./SubscriptionStatus";
import { UpgradeButton } from "./UpgradeButton";
import { PricingModal } from "../subscription/PricingModal";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

export const SubscriptionSection = ({ profileData, hasActiveSubscription }: {
  profileData: any;
  hasActiveSubscription: boolean;
}) => {
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const [isPricingOpen, setIsPricingOpen] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const { toast } = useToast();
  const { session } = useSession();

  const handleUpgradeClick = useCallback(() => {
    if (!session?.user) {
      toast({
        variant: "destructive",
        title: "Authentication Required",
        description: "Please sign in to upgrade your subscription.",
      });
      return;
    }
    setIsPricingOpen(true);
  }, [session?.user, toast]);

  const handleCancelSubscription = useCallback(async () => {
    if (!session?.user) {
      toast({
        variant: "destructive",
        title: "Authentication Required",
        description: "Please sign in to manage your subscription.",
      });
      return;
    }

    try {
      setIsCanceling(true);
      
      // Call your backend endpoint to cancel the subscription
      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        body: { user_id: session.user.id }
      });

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: "Subscription Cancelled",
        description: "Your subscription has been cancelled and will end at the end of the billing period.",
      });
      
      // You might want to refresh the profile data here
      window.location.reload();
      
    } catch (error: any) {
      console.error("Error cancelling subscription:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to cancel subscription. Please try again or contact support.",
      });
    } finally {
      setIsCanceling(false);
    }
  }, [session?.user, toast]);

  const handleManageSubscription = () => {
    // Direct link to Stripe billing portal
    window.location.href = "https://billing.stripe.com/p/login/aEUeX53IhaOu8g0aEE";
  }

  const daysRemaining = useMemo(() => {
    if (!profileData?.trial_end_date) return 0;
    return differenceInDays(new Date(profileData.trial_end_date), new Date());
  }, [profileData?.trial_end_date]);

  const isTrialExpired = useMemo(() => 
    daysRemaining <= 0 && !hasActiveSubscription,
    [daysRemaining, hasActiveSubscription]
  );

  // Show upgrade option unless user has an active paid subscription
  const showUpgradeOption = !profileData?.subscription_status || 
    profileData.subscription_status !== "active";

  return (
    <div className="space-y-4">
      <SubscriptionStatus 
        subscriptionDetails={{
          status: profileData?.subscription_status || undefined,
          currentPeriodEnd: profileData?.current_period_end || undefined
        }}
        trialEndDate={profileData?.trial_end_date || undefined}
      />

      {showUpgradeOption ? (
        <>
          <UpgradeButton 
            isCheckingSubscription={isCheckingSubscription}
            onClick={handleUpgradeClick}
            daysRemaining={daysRemaining}
          />
          <PricingModal 
            open={isPricingOpen} 
            onOpenChange={setIsPricingOpen}
            isTrialExpired={isTrialExpired}
          />
        </>
      ) : (
        <div className="flex flex-col md:flex-row gap-3 mt-4">
          <Button 
            onClick={handleManageSubscription} 
            className="flex-1 bg-gradient-to-r from-blue-400 to-indigo-500 hover:from-blue-500 hover:to-indigo-600 text-white transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            {"Manage Subscription"}
          </Button>
          <Button 
            onClick={handleCancelSubscription} 
            variant="outline" 
            className="flex-1 border-red-300 text-red-500 hover:bg-red-50 hover:text-red-600"
            disabled={isCanceling}
          >
            {isCanceling ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              "Cancel Subscription"
            )}
          </Button>
        </div>
      )}
    </div>
  );
};